import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, GripVertical, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESET_COLORS = ["#3b82f6", "#8b5cf6", "#06b6d4", "#f59e0b", "#eab308", "#10b981", "#ef4444", "#ec4899", "#64748b", "#84cc16"];

export default function PipelineSettings() {
  const [stages, setStages] = useState([]);
  const [triggerOptions, setTriggerOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", color: PRESET_COLORS[8], is_won: false, is_lost: false, auto_triggers: [] });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [draggingId, setDraggingId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [stagesRes, triggersRes] = await Promise.all([
        axios.get(`${API}/pipeline/stages`, { withCredentials: true }),
        axios.get(`${API}/pipeline/trigger-options`, { withCredentials: true }),
      ]);
      setStages(stagesRes.data);
      setTriggerOptions(triggersRes.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/pipeline/stages`, createForm, { withCredentials: true });
      toast.success("Stage angelegt");
      setShowCreate(false);
      setCreateForm({ name: "", color: PRESET_COLORS[8], is_won: false, is_lost: false, auto_triggers: [] });
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const handleSave = async (stageId) => {
    try {
      await axios.put(`${API}/pipeline/stages/${stageId}`, editForm, { withCredentials: true });
      toast.success("Gespeichert");
      setEditingId(null);
      reload();
    } catch (err) { toast.error("Fehler"); }
  };

  const handleDelete = async (stage) => {
    if (!window.confirm(`Stage "${stage.name}" wirklich löschen?\n\nLeads in dieser Stage werden auf die erste Stage verschoben.`)) return;
    try {
      await axios.delete(`${API}/pipeline/stages/${stage.id}`, { withCredentials: true });
      toast.success("Stage gelöscht");
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const handleReorder = async (newOrder) => {
    setStages(newOrder); // optimistic
    try {
      await axios.put(`${API}/pipeline/stages/reorder`, { order: newOrder.map(s => s.id) }, { withCredentials: true });
    } catch (err) { toast.error("Reihenfolge konnte nicht gespeichert werden"); reload(); }
  };

  const onDragOver = (e, targetId) => {
    e.preventDefault();
    if (!draggingId || draggingId === targetId) return;
    const newOrder = [...stages];
    const fromIdx = newOrder.findIndex(s => s.id === draggingId);
    const toIdx = newOrder.findIndex(s => s.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, moved);
    setStages(newOrder);
  };

  const startEdit = (s) => { setEditingId(s.id); setEditForm({ name: s.name, color: s.color, is_won: s.is_won, is_lost: s.is_lost, auto_triggers: s.auto_triggers || [] }); };

  const toggleTriggerEdit = (triggerId) => {
    setEditForm(f => {
      const cur = f.auto_triggers || [];
      return { ...f, auto_triggers: cur.includes(triggerId) ? cur.filter(t => t !== triggerId) : [...cur, triggerId] };
    });
  };

  const toggleTriggerCreate = (triggerId) => {
    setCreateForm(f => {
      const cur = f.auto_triggers || [];
      return { ...f, auto_triggers: cur.includes(triggerId) ? cur.filter(t => t !== triggerId) : [...cur, triggerId] };
    });
  };

  // Welche Trigger sind schon woanders zugewiesen? (für UI-Hinweis)
  const triggerToStageMap = {};
  stages.forEach(s => (s.auto_triggers || []).forEach(t => { triggerToStageMap[t] = triggerToStageMap[t] || []; triggerToStageMap[t].push(s.name); }));

  if (loading) return <p className="text-sm text-muted-foreground">Laden...</p>;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="pipeline-settings">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-0.5">Pipeline · Stages</p>
          <h2 className="font-heading text-xl font-black tracking-tight">Stages konfigurieren</h2>
          <p className="text-sm text-muted-foreground mt-1">Drag&Drop zum Sortieren · Klick auf Stage zum Bearbeiten</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="rounded-sm gap-1.5" size="sm" data-testid="add-stage-btn">
          <Plus size={13} /> Neue Stage
        </Button>
      </div>

      <div className="space-y-1">
        {stages.map(s => (
          <div
            key={s.id}
            draggable={editingId !== s.id}
            onDragStart={() => setDraggingId(s.id)}
            onDragEnd={() => { setDraggingId(null); handleReorder(stages); }}
            onDragOver={(e) => onDragOver(e, s.id)}
            className={`bg-card border border-border rounded-sm ${draggingId === s.id ? "opacity-50" : ""} ${editingId === s.id ? "border-foreground" : ""}`}
            style={{ borderLeft: `4px solid ${s.color}` }}
            data-testid={`stage-row-${s.id}`}
          >
            <div className="p-3 flex items-center gap-3">
              <GripVertical size={14} className="text-muted-foreground cursor-grab" />
              {editingId === s.id ? (
                <>
                  <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="rounded-sm h-8 max-w-xs" data-testid={`edit-name-${s.id}`} />
                  <div className="flex gap-1 flex-1">
                    {PRESET_COLORS.map(c => (
                      <button key={c} type="button" onClick={() => setEditForm(f => ({ ...f, color: c }))}
                        className={`w-6 h-6 rounded-full border-2 ${editForm.color === c ? "border-foreground" : "border-border"}`}
                        style={{ background: c }} aria-label={c}
                      />
                    ))}
                  </div>
                  <Button size="sm" onClick={() => handleSave(s.id)} className="rounded-sm h-8 gap-1" data-testid={`save-stage-${s.id}`}><Save size={11} /> Speichern</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="rounded-sm h-8"><X size={13} /></Button>
                </>
              ) : (
                <>
                  <button onClick={() => startEdit(s)} className="flex-1 text-left flex items-center gap-3 flex-wrap" data-testid={`stage-edit-${s.id}`}>
                    <span className="font-semibold text-sm">{s.name}</span>
                    {s.is_won && <span className="text-[10px] uppercase tracking-widest font-bold bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">Gewonnen</span>}
                    {s.is_lost && <span className="text-[10px] uppercase tracking-widest font-bold bg-red-100 text-red-800 px-1.5 py-0.5 rounded">Verloren</span>}
                    {(s.auto_triggers || []).length > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground" title="Auto-Triggers">
                        <span className="opacity-60">⚡</span>
                        {(s.auto_triggers || []).map(t => {
                          const opt = triggerOptions.find(o => o.id === t);
                          return (
                            <span key={t} className="px-1.5 py-0.5 rounded bg-muted/60 font-medium">
                              {opt?.label || t}
                            </span>
                          );
                        })}
                      </span>
                    )}
                  </button>
                  {!s.is_won && !s.is_lost && (
                    <button onClick={() => handleDelete(s)} className="p-1.5 text-muted-foreground hover:text-destructive rounded-sm hover:bg-muted" data-testid={`delete-stage-${s.id}`}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </>
              )}
            </div>
            {/* Trigger-Editor (nur im Edit-Mode sichtbar) */}
            {editingId === s.id && (
              <div className="px-3 pb-3 pt-1 border-t border-border bg-muted/20">
                <p className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2 mt-2">
                  Auto-Trigger · Lead landet hier wenn:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {triggerOptions.map(opt => {
                    const isOn = (editForm.auto_triggers || []).includes(opt.id);
                    const otherStage = (triggerToStageMap[opt.id] || []).find(n => n !== s.name);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => toggleTriggerEdit(opt.id)}
                        className={`text-[11px] px-2 py-1 rounded-sm border transition-colors ${
                          isOn
                            ? "bg-foreground text-background border-foreground"
                            : "bg-card text-foreground border-border hover:border-foreground/50"
                        }`}
                        title={otherStage ? `Trigger ist auch in "${otherStage}" — nur die letzte Stage greift.` : ""}
                        data-testid={`trigger-toggle-${s.id}-${opt.id}`}
                      >
                        {isOn && "✓ "}{opt.label}
                        {!isOn && otherStage && <span className="ml-1 opacity-50">→ {otherStage}</span>}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground mt-2 leading-snug">
                  Tipp: Wenn ein Trigger in mehreren Stages aktiv ist, greift die <strong>späteste</strong> in der Reihenfolge — sodass z.&nbsp;B. ein erfolgreicher Anruf nicht in „Wiedervorlage" landet.
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-bold">Neue Stage</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3" data-testid="create-stage-form">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Name *</Label>
              <Input required value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} className="rounded-sm" data-testid="new-stage-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Farbe</Label>
              <div className="flex gap-2 mt-1">
                {PRESET_COLORS.map(c => (
                  <button key={c} type="button" onClick={() => setCreateForm(f => ({ ...f, color: c }))}
                    className={`w-7 h-7 rounded-full border-2 ${createForm.color === c ? "border-foreground" : "border-border"}`}
                    style={{ background: c }}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Auto-Trigger</Label>
              <p className="text-[11px] text-muted-foreground mt-1 mb-2">Wann soll ein Lead automatisch in diese Stage rutschen?</p>
              <div className="flex flex-wrap gap-1.5">
                {triggerOptions.map(opt => {
                  const isOn = (createForm.auto_triggers || []).includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => toggleTriggerCreate(opt.id)}
                      className={`text-[11px] px-2 py-1 rounded-sm border transition-colors ${
                        isOn
                          ? "bg-foreground text-background border-foreground"
                          : "bg-card text-foreground border-border hover:border-foreground/50"
                      }`}
                    >
                      {isOn && "✓ "}{opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="rounded-sm">Abbrechen</Button>
              <Button type="submit" className="rounded-sm" data-testid="create-stage-submit">Anlegen</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
